import * as Yup from 'yup';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import Loader from '@cpg-its/ads.common.loading-circle';
import { Hidden } from '@material-ui/core';
import Banner from '@cpg-its/ads.containers.banner';
import { FormWrapper } from '../../../../../global/components/form-wrapper';
import { NewHireStep } from '../models/new-hire-model';
import { ClergyNewHireFormValues, EmploymentType, getInitialNewHireValues, newHireFormValidation, NewHireFormValues, NonStipNewHireFormValues } from '../models/new-hire-form-model';
import { NewHirePersonalInfoFields } from './new-hire-personal-info-fields';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { NewHireProps, noSelectPersonId } from '../../../models/arc-epi-props';
import { NewHireNonStipPositionFields } from './non-stip-position-fields';
import { NewHireStipEmploymentCompFields } from './stip-employment-comp-fields';
import { PersonSearchContext, PersonSearchState, SearchPerson } from '../../person-search/person-search-model';
import { TcsmEpiApiState, TcsmStates } from '../../../../../root/tcsm-model';
import { PersonSearchToastCombo } from '../../person-search/person-search-toast-combo';
import { PersonSearchResults } from '../../person-search/person-search-results';
import { apiErrorClear } from '../../../../../global/ducks/base-api';
import { setNewHireStep } from '../new-hire-slice';
import { shouldContinueButtonBeDisabled, transformSearchPersonToNewHireFormValues } from '../../person-search/oscrr-helpers';
import { NewHireExternalPersonReview } from './new-hire-external-person-review';
import { Employee, EmploymentState } from '../../employment/employment-model';
import { NewHireInternalPersonReview } from './new-hire-internal-person-review';
import { NewHireProgressBar } from './new-hire-progress-bar';
import { PageHeader } from '../../../../../global/components/layout/page-header';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { PageContainer } from '../../../../../global/components/layout/page-container';
import { MainContent } from '../../../../../global/components/layout/main-content-container';
import { SideBarContent } from '../../../../../global/components/layout/sidebar-content';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';
import { useApiResponseError } from '../../../../../global/hooks/useApiError';
import { useGpsType } from '../../../../../global/hooks/useGpsType';
import { GuidedProcessSupportBlock } from '../../../../../global/components/guided-process-support-block';
import { GpsType } from '../../../../../global/components/layout/layout-models';
import { FormTypes, matchedPersonField } from '../../../../account-info/models/form-models';
import { Person } from '../../../../../global/models/person-model';
import { InstitutionState } from '../../institutions/institution-model';
import { getEmployeesList } from '../../../../../global/ducks/benadmin-api';
import { dispatchSetEmployeeList } from '../../employment/employment-slice';
import { setEmployeeListLoaded } from '../../institutions/institution-slice';
import { NewHireEmploymentType } from './new-hire-employment-type';
import { NewHireClergyType } from './new-hire-clergy-type';
import { NewHireStipEmploymentCompensationReview } from './stip-employment-comp-fields-review';
import { NewHireNonStipEmploymentCompensationReview } from './non-stip-employment-comp-fields-review';
import { PersonalInformationApp } from '../../../../account-info/features/personal-info/personal-info';
import { PersonalInfoNewHireWizard } from '../../../../account-info/features/personal-info/wizard/personal-info-new-hire-wizard';
import { DemographicAppKeys } from '../../../../../global/models/demographics-model';


interface Props {
	currentStep: NewHireStep;
	labelSettings: LabelSettingProps;
	newHireProps: NewHireProps;
	nonStip: boolean;
	onCancel: (formProps: FormikProps<NewHireFormValues>, employmentType?: boolean, nonStipEmployment?: boolean) => any;
	onSubmit: (values: NewHireFormValues, formikHelpers: FormikHelpers<NewHireFormValues>, selectedPersonGuid: string, internalEmployee: Employee) => any;
	currentPerson?: Person;
	isReview: boolean;
}
export function NewHireForm(props: Props) {
	const groupName = matchedPersonField;
	const { currentStep, labelSettings, newHireProps, onCancel, onSubmit, nonStip, currentPerson, isReview } = props;

	const dispatch = useDispatch();
	const history = useHistory();
	const routeMatch = useRouteMatch();
	const personSearchKey = useContext(PersonSearchContext);
	const personSearchState = useSelector<TcsmStates, PersonSearchState>(state => state.personSearchState[personSearchKey]);
	const searchResultsRef = useRef<HTMLDivElement>();
	const employmentState = useSelector<TcsmStates, EmploymentState>(state => state?.employmentState);
	const internalEmployees = employmentState?.employeeList;

	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);

	const hasSearchResults: boolean = personSearchState?.searchResults?.length > 0;

	const initialValues = getInitialNewHireValues(currentPerson, nonStip);
	const validationSchema = isReview ? Yup.object().shape({}) : newHireFormValidation(labelSettings, newHireProps, currentStep, nonStip, hasSearchResults);
	const apiErrors = useApiResponseError(nonStip ? FormTypes.NON_STIP_NEW_HIRE : FormTypes.STIP_NEW_HIRE);

	const [selectedPerson, setSelectedPerson] = useState(currentPerson ? currentPerson.person_guid : null);
	const [selectedInternalEmployee, setInternalEmployee] = useState(null);
	const [cachedFormValues, setCachedFormValues] = useState(null);

	function OnPersonalInfoReviewSubmit() {
		if (employmentState?.currentEmployee?.employmentType === EmploymentType.Clergy) {
			dispatch(setNewHireStep(NewHireStep.EmploymentCompensationClergy));
		}

		dispatch(setNewHireStep(NewHireStep.EmploymentCompensationLay));
	}

	const personalInfoWizard = new PersonalInfoNewHireWizard(epiProps, null, DemographicAppKeys.arc, dispatch, history, nonStip, OnPersonalInfoReviewSubmit);

	function CancelButtonTextInReview() {
		if (currentStep === NewHireStep.EmploymentCompensationClergy ||
			currentStep === NewHireStep.EmploymentCompensationLay) {
			return newHireProps?.employmentCompensationBackToPersonalInformationButtonText;
		}

		if (currentStep === NewHireStep.PersonalInfo) {
			return newHireProps?.cancelButtonText;
		}

		return newHireProps?.goBackButtonText;
	}

	function CancelButtonText() {
		if (isReview) {
			return CancelButtonTextInReview();
		}

		if (currentStep === NewHireStep.EmploymentType) {
			return newHireProps?.employmentTypeBackButtonText;
		}

		if (currentStep === NewHireStep.ClergyType) {
			return newHireProps?.clergyTypeBackButtonText;
		}

		return currentStep === NewHireStep.PersonalInfo ? newHireProps?.cancelButtonText : newHireProps?.goBackButtonText;
	}

	function SubmitButtonText(formProps: FormikProps<NewHireFormValues>) {
		const clergyNewHireFormValues: ClergyNewHireFormValues = formProps?.values as ClergyNewHireFormValues;
		const isClergy = clergyNewHireFormValues.employment_type === EmploymentType.Clergy;

		if (currentStep === NewHireStep.EmploymentType) {
			if (nonStip) {
				return newHireProps?.nonStipContinueButtonText;
			}

			return isClergy ? newHireProps?.employmentTypeContinueClergyButtonText : newHireProps?.employmentTypeContinueButtonText;
		}

		if (currentStep === NewHireStep.ClergyType) {
			return newHireProps?.clergyTypeContinueButtonText;
		}

		if (currentStep === NewHireStep.PersonReviewInternal) {
			return newHireProps?.internalEmployeeContinueButtonText || 'View Active Employment';
		}

		if (currentStep === NewHireStep.HireDetails ||
			currentStep === NewHireStep.EmploymentCompensationClergy ||
			currentStep === NewHireStep.EmploymentCompensationLay) {
			return nonStip ? newHireProps?.nonStipSubmitButtonText : newHireProps?.stipSubmitButtonText;
		}

		return nonStip ? newHireProps?.nonStipContinueButtonText : newHireProps?.stipContinueButtonText;
	}

	const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);
	const institutionId = institutionState?.currentInstitution?.org_guid;

	const [preventButtonDisableValidation, setPreventButtonDisableValidation] = useState(false);

	useEffect(() => {
		setPreventButtonDisableValidation((currentStep === NewHireStep.PersonReviewExternal) || (currentStep === NewHireStep.PersonReviewInternal));
	}, [currentStep]);

	useEffect(() => {
		if (institutionId) {
			dispatch(getEmployeesList(institutionId, (response) => {
				dispatchSetEmployeeList(dispatch)(response);
				dispatch(setEmployeeListLoaded());
			}));
		}
	}, [institutionId]);

	const getPageHeader = (employmentType: string) => {
		if (currentStep === NewHireStep.EmploymentType) {
			return newHireProps?.nonStipEmploymentTypeTitle;
		}

		if (currentStep === NewHireStep.ClergyType) {
			return newHireProps?.clergyTypeTitle;
		}

		if (currentStep === NewHireStep.HireDetails && !nonStip) {
			return newHireProps?.employmentCompensationHeader;
		}

		if (nonStip &&
			(currentStep === NewHireStep.HireDetails ||
				currentStep === NewHireStep.EmploymentCompensationClergy ||
				currentStep === NewHireStep.EmploymentCompensationLay)) {
			return employmentType === EmploymentType.Lay ? newHireProps?.nonStipLayPositionHeader : newHireProps?.nonStipClergyPositionHeader;
		}

		if (!nonStip && (currentStep === NewHireStep.EmploymentCompensationClergy ||
			currentStep === NewHireStep.EmploymentCompensationLay)) {
			return newHireProps?.employmentCompensationHeader;
		}

		return newHireProps?.personalInfoPageHeader;
	};
	const getGpsType = () => {
		if (currentStep === NewHireStep.PersonalInfo) {
			return GpsType.NEW_HIRE_DEMO;
		}

		if (currentStep === NewHireStep.PersonReviewInternal) {
			return GpsType.NEW_HIRE_SEARCH_INSIDE;
		}

		if (currentStep === NewHireStep.PersonReviewExternal) {
			return GpsType.NEW_HIRE_SEARCH_OUTSIDE;
		}

		if (currentStep === NewHireStep.ClergyType) {
			return GpsType.NEW_HIRE_CLERGY_TYPE;
		}

		if (currentStep === NewHireStep.EmploymentType) {
			return GpsType.NEW_HIRE_EMPLOYMENT;
		}

		if (!nonStip &&
			(currentStep === NewHireStep.EmploymentCompensationClergy ||
				currentStep === NewHireStep.EmploymentCompensationLay)) {
			return GpsType.EMPLOYMENT_COMPENSATION;
		}

		if (nonStip) {
			return GpsType.NEW_HIRE_NON_STIP;
		}

		return GpsType.NEW_HIRE_STIP;
	};

	const gpsItem = useGpsType(getGpsType());

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const renderReviewContent = (formProps: FormikProps<NewHireFormValues>) => {

		if (nonStip) {
			return <NewHireNonStipEmploymentCompensationReview newHireProps={newHireProps} />;
		}

		return <NewHireStipEmploymentCompensationReview newHireProps={newHireProps} />;
	};

	const renderFormContent = (formProps: FormikProps<NewHireFormValues>) => {
		if (isReview) {
			return renderReviewContent(formProps);
		}

		if (currentStep === NewHireStep.PersonalInfo) {
			return <NewHirePersonalInfoFields formProps={formProps} labelSetting={labelSettings} />;
		}

		if (currentStep === NewHireStep.PersonReviewInternal) {
			return <NewHireInternalPersonReview newHireProps={newHireProps} employee={selectedInternalEmployee} nonStip={nonStip} />;
		}

		if (currentStep === NewHireStep.PersonReviewExternal) {
			return <NewHireExternalPersonReview formProps={formProps} labelSetting={labelSettings} newHireProps={newHireProps} nonStip={nonStip} />;
		}

		if (currentStep === NewHireStep.EmploymentType) {
			return <NewHireEmploymentType formProps={formProps} newHireProps={newHireProps} />;
		}

		if (currentStep === NewHireStep.ClergyType) {
			return <NewHireClergyType formProps={formProps} newHireProps={newHireProps} labelSettings={labelSettings} />;
		}

		if (nonStip) {
			return <NewHireNonStipPositionFields formProps={formProps} newHireProps={newHireProps} />;
		}

		return <NewHireStipEmploymentCompFields formProps={formProps} newHireProps={newHireProps} />;
	};

	const getSubmitHelperText = () => {

		if (currentStep === NewHireStep.EmploymentType) {
			return newHireProps?.employmentTypeButtonHelperText;
		}

		if (currentStep === NewHireStep.ClergyType) {
			return newHireProps?.clergyTypeButtonHelperText;
		}

		if (currentStep === NewHireStep.HireDetails ||
			currentStep === NewHireStep.EmploymentCompensationClergy ||
			currentStep === NewHireStep.EmploymentCompensationLay) {
			return nonStip ? newHireProps?.nonStipSubmitHelperText : newHireProps?.stipSubmitHelperText;
		}

		return null;
	};

	const onPersonSelect = (person: SearchPerson, formProps: FormikProps<NewHireFormValues>, values: NewHireFormValues) => {
		setCachedFormValues(values);
		dispatch(apiErrorClear());
		formProps.setValues(transformSearchPersonToNewHireFormValues(person, nonStip));
		formProps.setTouched({});

		// selected search person is internal
		const internalEmployee = internalEmployees?.find(employee => employee.guid === person.guid);
		if (internalEmployee) {
			setInternalEmployee(internalEmployee);
			dispatch(setNewHireStep(NewHireStep.PersonReviewInternal));

		} else {
			setSelectedPerson(person?.guid);
			dispatch(setNewHireStep(NewHireStep.HireDetails));
		}
	};

	function onInternalSubmit(values: NewHireFormValues, formikHelpers: FormikHelpers<NewHireFormValues>, selectedPersonGuid: string, internalEmployee: Employee) {

		const formProps: FormikProps<NewHireFormValues> = formikHelpers as FormikProps<NewHireFormValues>;

		if (currentStep === NewHireStep.PersonalInfo && values) {
			const selectedPersonId = values[groupName];

			if (selectedPersonId &&
				selectedPersonId !== noSelectPersonId &&
				selectedPersonId !== selectedPerson &&
				personSearchState?.searchResults?.length > 0) {

				const selectedPersonGrid = personSearchState?.searchResults?.find(employee => employee.guid === selectedPersonId);

				if (selectedPersonGrid) {
					onPersonSelect(selectedPersonGrid, formProps, values);
					return;
				}
			}
		}

		onSubmit(values, formikHelpers, selectedPersonGuid, internalEmployee);
	}

	const onNavigateToTop = () => {
		window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
	};

	const cancelAction = (formProps: FormikProps<NewHireFormValues>) => {
		if (cachedFormValues) {
			formProps.setValues(cachedFormValues);
			formProps.setTouched({});
		}

		if (currentStep === NewHireStep.PersonalInfo) {
			formProps.setFieldValue(groupName, null);
		}

		// clear out person search states
		setCachedFormValues(null);
		setSelectedPerson(null);
		setInternalEmployee(null);

		// handle rest in parent
		onCancel(formProps, currentStep === NewHireStep.EmploymentType, nonStip && currentStep === NewHireStep.HireDetails);
	};

	if (!institutionState.employeeListLoaded) {
		return (<Loader />);
	}

	if (isReview && currentStep === NewHireStep.PersonalInfo) {
		return (
			<>
				<NewHireProgressBar
					subProgressName={employmentState?.currentEmployee?.employmentType}
					nonStip={nonStip}
					personalInfoClick={() => onCancel(null, true)}
					employmentTypeClick={() => onCancel(null, false, true)}
					clergyTypeClick={() => onCancel(null)}
				/>
				<PageContainer>
					<MainContent>
						<PageHeader
							headerText={getPageHeader(employmentState?.currentEmployee?.employmentType)}
						/>
						<Hidden mdUp>
							<SideBarContent>
								<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
							</SideBarContent>
						</Hidden>

						<PersonalInformationApp customPersonalInfoWizard={personalInfoWizard} basePath={routeMatch.path} />

					</MainContent>
					<Hidden smDown>
						<SideBarContent>
							<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
						</SideBarContent>
					</Hidden>
				</PageContainer>
			</>
		);
	}

	return (
		<>
			<Formik
				initialValues={initialValues}
				onSubmit={(values, formikHelpers) => onInternalSubmit(values, formikHelpers, selectedPerson, selectedInternalEmployee)}
				validationSchema={validationSchema}
				enableReinitialize
				initialErrors={apiErrors}
			>
				{(formProps: FormikProps<NewHireFormValues>) => {
					return (
						<>
							<NewHireProgressBar
								subProgressName={(formProps?.values as NonStipNewHireFormValues)?.employment_type}
								nonStip={nonStip}
								personalInfoClick={() => onCancel(formProps, true)}
								employmentTypeClick={() => onCancel(formProps, false, true)}
								clergyTypeClick={() => onCancel(null)}
							/>
							<PageContainer>
								<MainContent>
									<PageHeader
										headerText={getPageHeader((formProps?.values as NonStipNewHireFormValues).employment_type)}
									/>
									<Hidden mdUp>
										<SideBarContent>
											<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
										</SideBarContent>
									</Hidden>
									<Form>
										<FormWrapper
											formikProps={formProps}
											cancelText={CancelButtonText()}
											submitText={SubmitButtonText(formProps)}
											onCancel={() => cancelAction(formProps)}
											helperText={getSubmitHelperText()}
											preventButtonDisableValidation={isReview || preventButtonDisableValidation}
											submitDisabled={preventButtonDisableValidation && shouldContinueButtonBeDisabled(formProps?.values?.matched_person, personSearchState?.searchResults, !employmentState?.currentEmployee)}
											personSearchToast={
												<PersonSearchToastCombo
													searchResultsRef={searchResultsRef}
													renderResultsMessaging={currentStep === NewHireStep.PersonalInfo && personSearchState.searchResults?.length > 0 && !isReview && !formProps?.values?.matched_person}
												/>
											}
											personSearchResults={
												currentStep === NewHireStep.PersonalInfo && !isReview &&
												<PersonSearchResults
													groupName={groupName}
													results={personSearchState?.searchResults}
													backToTopAction={onNavigateToTop}
													resultsRef={searchResultsRef} />
											}
											onlyContent={isReview && currentStep === NewHireStep.PersonalInfo}
										>
											{
												// only show message for stip on personal info page
												!nonStip && currentStep === NewHireStep.PersonalInfo &&
												<ContentSpacer><Banner
													banner={{
														type: 'warning',
														label: <div className='rich-text'><WysiwygWithReactLink content={newHireProps?.stipImportantMessage} /></div>,
													}} />
												</ContentSpacer>
											}

											{renderFormContent(formProps)}
										</FormWrapper>
									</Form>

								</MainContent>
								<Hidden smDown>
									<SideBarContent>
										<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
									</SideBarContent>
								</Hidden>
							</PageContainer>
						</>
					);
				}}

			</Formik>
		</>
	);
}


NewHireForm.defaultProps = {
	currentPerson: null,
};